/**
 * UserContext — provee autenticación usando Supabase Auth
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import type { Profile } from "@/lib/db/schema";
import { supabase } from "@/lib/auth";

interface User {
  id: string;
  email: string;
  role?: string;
}

interface UserContextValue {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (email: string, password: string, displayName: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  refreshProfile: () => Promise<void>;
}

const UserContext = createContext<UserContextValue | null>(null);

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) return;

      const res = await fetch("/api/profile", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const json = await res.json() as { profile?: Profile } & Partial<Profile>;
      const p = json.profile ?? json as Profile;
      if (p?.userId) {
        setProfile(p);
        if (p.role) {
          setUser(prev => prev ? { ...prev, role: p.role } : prev);
        }
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  }, []);

  // Listen to Supabase auth state changes
  useEffect(() => {
    // Check initial session
    supabase.auth.getSession().then(({ data }) => {
      if (data.session?.user) {
        const u = data.session.user;
        setUser({ id: u.id, email: u.email ?? '' });
        fetchProfile().finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    // Subscribe to auth changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser({ id: session.user.id, email: session.user.email ?? '' });
        fetchProfile();
      } else {
        setUser(null);
        setProfile(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const login = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return { success: false, error: error.message };
      if (data.user) {
        setUser({ id: data.user.id, email: data.user.email ?? '' });
        await fetchProfile();
        return { success: true };
      }
      return { success: false, error: 'Login failed' };
    } catch (error: unknown) {
      return { success: false, error: error instanceof Error ? error.message : 'Login failed' };
    }
  };

  const register = async (email: string, password: string, displayName: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { display_name: displayName } },
      });
      if (error) return { success: false, error: error.message };
      if (data.user) {
        setUser({ id: data.user.id, email: data.user.email ?? '' });
        // New user — no profile yet, CompleteProfile will create it
        return { success: true };
      }
      return { success: false, error: 'Registration failed' };
    } catch (error: unknown) {
      return { success: false, error: error instanceof Error ? error.message : 'Registration failed' };
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  };

  const refreshProfile = async () => {
    await fetchProfile();
  };

  const value: UserContextValue = {
    user,
    profile,
    loading,
    login,
    register,
    logout,
    refreshProfile,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUserContext() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
}
